import { useEffect, useRef } from "react";

const SRC = "https://news.google.com/swg/js/v1/publisher.js";

type PreferredSourceApi = { init: (opts?: Record<string, unknown>) => void };
declare global {
  interface Window {
    PREFERRED_SOURCE?:
      | { push: (cb: (api: PreferredSourceApi) => void) => void; api?: PreferredSourceApi }
      | Array<(api: PreferredSourceApi) => void>;
  }
}

/**
 * Google "Preferred Sources" button.
 *
 * publisher.js only auto-scans the DOM once, when it first loads: after that it
 * short-circuits because window.PREFERRED_SOURCE already exists, so re-adding
 * the <script> does nothing. Instead we use its queue/api and call init() again
 * for containers that appear later (client-side navigation, extra instances).
 */
function useInitPreferredSource(
  containerRef: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const rendered = () =>
      !!containerRef.current?.querySelector("iframe, button, a, [role='button']");

    const ensureScript = () => {
      if (document.querySelector(`script[src="${SRC}"]`)) return;
      const s = document.createElement("script");
      s.async = true;
      s.src = SRC;
      document.head.appendChild(s);
    };

    const runInit = () => {
      const ps = window.PREFERRED_SOURCE;
      if (!ps) {
        // Queue our init so it runs the moment publisher.js evaluates.
        window.PREFERRED_SOURCE = [(api) => api.init()];
        ensureScript();
        return;
      }
      if (Array.isArray(ps)) {
        ps.push((api) => api.init());
        return;
      }
      if (ps.api) ps.api.init();
      else ps.push((api) => api.init());
    };

    const tick = () => {
      if (cancelled || rendered()) return;
      attempts += 1;
      runInit();
      if (attempts < 30) window.setTimeout(tick, 400);
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [containerRef]);
}


export function PreferredSourceButton({
  heading = "Make Us Your Preferred Source",
  blurb = "Add Phoenix Plumbing Co. as a preferred source on Google so our local plumbing advice shows up first in your results.",
  className = "",
}: {
  heading?: string;
  blurb?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useInitPreferredSource(ref);

  return (
    <div
      className={`rounded-xl border-2 border-accent/60 bg-accent/5 p-5 sm:p-6 ${className}`}
    >
      <h2 className="text-lg sm:text-xl font-extrabold text-secondary">{heading}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{blurb}</p>
      <div ref={ref} className="mt-4 min-h-10">
        <div
          suppressHydrationWarning
          {...({ "google-add-preferred-source-btn": "" } as any)}
        />
      </div>
    </div>
  );
}

/** Compact variant for the footer — just the button, no card. */
export function PreferredSourceButtonCompact({
  className = "",
}: {
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useInitPreferredSource(ref);

  return (
    <div ref={ref} className={className}>
      <div
        suppressHydrationWarning
        {...({ "google-add-preferred-source-btn": "" } as any)}
      />
    </div>
  );
}
